import React, { useEffect, useState } from 'react';
import { Form, Input, Modal, Upload, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import "../ModalCom/profileModal.css";
import { put } from '../../services/ApiRouting';
import { getToken, setProfile } from '../../services/localStorageServices';

const ProfileModal = ({ isModalOpen, handleOk, handleCancel, onProfileUpdate }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [profileData, setProfileData] = useState({});
    const [fileList, setFileList] = useState([]);

    useEffect(() => {
        if (isModalOpen) {
            const storedProfile = JSON.parse(localStorage.getItem('profile')) || {};
            setProfileData(storedProfile);

            // Populate form values
            form.setFieldsValue({
                name: storedProfile.name || '',
                email: storedProfile.email || '',
                phone: storedProfile.phone || storedProfile.contact || ''
            });

            // Handle current profile image preview
            const currentImg = storedProfile.profile_image_url || storedProfile.profile_image || storedProfile.user_image;
            if (currentImg) {
                setFileList([
                    {
                        uid: '-1',
                        name: 'profile.webp',
                        status: 'done',
                        url: currentImg.startsWith('http') ? currentImg : `https://1fiveglobal.in${currentImg}`,
                    },
                ]);
            } else {
                setFileList([]);
            }
        }
    }, [isModalOpen, form]);

    const onFinish = async (values) => {
        setLoading(true);
        const userId = profileData.id || profileData.user_id;
        const { access_token: accessToken } = getToken();
        if (!userId) {
            message.error("User ID missing. Please login again.");
            setLoading(false);
            return;
        }

        if (!accessToken) {
            message.error("Access denied. No token provided. Please log in again.");
            setLoading(false);
            return;
        }

        // Construct multipart/form-data payload matching Postman keys
        const formDataToSend = new FormData();
        formDataToSend.append('name', values.name);
        formDataToSend.append('email', values.email);
        formDataToSend.append('phone', values.phone);

        // Append file using key 'profile_image' if a new file was uploaded
        if (fileList.length > 0 && fileList[0].originFileObj) {
            formDataToSend.append('profile_image', fileList[0].originFileObj);
        }

        try {
            // Send PUT request to user update endpoint with Authorization header
            const response = await put(`/users/${userId}`, formDataToSend, {
                headers: { 
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (response?.success !== false) {
                const updatedUser = response.user || response.data || { ...profileData, ...values };

                // 1. Save updated user object to local storage
                setProfile(updatedUser);

                // 2. Update local state inside modal
                setProfileData(updatedUser);

                // 3. Notify parent component to refresh UI across the app
                if (onProfileUpdate) {
                    onProfileUpdate(updatedUser);
                }

                message.success(response.message || 'Profile updated successfully');
                handleOk();
            } else {
                message.error(response?.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error('Error updating user:', error);
            message.error(error?.response?.data?.message || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal 
            title="Edit Profile" 
            open={isModalOpen} 
            onOk={form.submit} 
            onCancel={handleCancel} 
            confirmLoading={loading}
            width={700}
        >
            <div className='profile-modal-main-con'>
                <Form layout='vertical' form={form} onFinish={onFinish}>
                    <div className='profile-modal-name-and-surname-con'>
                        <Form.Item 
                            name="name" 
                            label="Name" 
                            rules={[{ required: true, message: 'Please enter your name.' }]}
                        >
                            <Input placeholder='Enter Name' />
                        </Form.Item>

                        <Form.Item 
                            name="email" 
                            label="Email" 
                            rules={[
                                { required: true, message: 'Please enter your email.' },
                                { type: 'email', message: 'Please enter a valid email address.' }
                            ]}
                        >
                            <Input placeholder='Enter Email' />
                        </Form.Item>

                        <Form.Item
                            name="phone"
                            label="Phone Number"
                            rules={[
                                { required: true, message: 'Please enter your phone number.' }
                            ]}
                        >
                            <Input placeholder='Enter Phone Number' />
                        </Form.Item>

                        <Form.Item label="Profile Image">
                            <Upload
                                listType="picture-card"
                                fileList={fileList}
                                beforeUpload={() => false}
                                onChange={({ fileList: newFileList }) => setFileList(newFileList.slice(-1))}
                                onRemove={() => setFileList([])}
                            >
                                {fileList.length === 0 && (
                                    <button
                                        style={{ color: 'inherit', cursor: 'pointer', border: 0, background: 'none' }}
                                        type="button"
                                    >
                                        <PlusOutlined />
                                        <div style={{ marginTop: 8 }}>Upload</div>
                                    </button>
                                )}
                            </Upload>
                        </Form.Item>
                    </div>
                </Form>
            </div>
        </Modal>
    );
};

export default ProfileModal;